<template>
  <b-container>
    <h1 v-translate="{step}">New Courier <small>[step %{step} of 2]</small></h1>
    <hr>
    <b-alert :show="errors !== ''" dismissible variant="danger">
      <h5 v-translate>There are errors!</h5>
      <ul>
        <span v-html="errors"></span>
      </ul>
    </b-alert>
    <b-row v-show="step === 1">
      <b-col>
        <b-card>
          <form @submit.prevent="createCourier">
            <div class="row">
              <h3 class="col-12" v-translate>Basic Info</h3>
              <div class="form-group col-md-12">
                <label class="control-label" v-translate>Name</label>
                <input class="form-control" type="text" v-model="user.name" required>
              </div>
              <div class="form-group col-md-12">
                <label class="control-label" v-translate>Email</label>
                <input class="form-control" type="email" v-model="user.email" required>
              </div>
              <div class="form-group col-md-12">
                <label class="control-label" v-translate>TIN</label>
                <input class="form-control" type="text" v-model="user.tin">
                <b-form-text v-translate>The courier's Fiscal Code/Number</b-form-text>
              </div>
              <div class="form-group col-md-12">
                <label class="control-label" v-translate>Password</label>
                <password ref="pwd_check" :min="passwordMinLength" :placeholder="$gettext('New Password')" v-model="user.password" inputClass="form-control" @input="validatePassword" @score="newPasswordScore = $event" :min-score="passwordMinScore" />
                <b-form-text id="inputLiveHelp" v-translate="{minLength: passwordMinLength}">%{minLength} characters minimum, must contain upper and lower case letters, numbers and symbols</b-form-text>
              </div>
              <div class="form-group col-md-12">
                <label class="control-label" v-translate>Repeat password</label>
                <b-form-input aria-describedby="inputLiveHelp inputLiveFeedback" :state="samePassword" type="password" v-model="passwordConfirm" @input="validatePassword" autocomplete="new-password" required></b-form-input>
                <b-form-invalid-feedback id="inputLiveFeedback" v-translate>Passwords don't match</b-form-invalid-feedback>
              </div>
              <div class="form-group col-md-12">
                <label class="control-label" v-translate>Country</label>
                <b-form-select v-model="user.country_iso_code" :options="countriesSelect" class="mb-3" required>
                  <template slot="first">
                    <option :value="null" v-translate>Select a country</option>
                  </template>
                </b-form-select>
              </div>
            </div>
            <div class="row text-left">
              <div class="form-group col-12 form-actions">
                <label class="col-md-3 control-label"></label>
                <div class="col-md-8">
                  <b-button variant="primary" size="lg" type="submit" :disabled="!samePassword || newPasswordScore < passwordMinScore" v-translate>Step 2</b-button>
                </div>
              </div>
            </div>
          </form>
        </b-card>
      </b-col>
    </b-row>
    <b-row v-show="step === 2">
      <b-col>
        <b-card>
          <b-alert :show="dismissCountDown" dismissible variant="success" @dismissed="dismissCountDown=0" @dismiss-count-down="countDownChanged">
            <h5 v-translate>Profile was updated!</h5>
          </b-alert>
          <form @submit.prevent="updateCourier">
            <div class="row">
              <h3 class="col-12" v-translate>Aditional Info</h3>
              <div class="form-group col-md-12">
                <label class="control-label" v-translate>Profile Picture</label>
                <div v-show="user.profile_picture">
                  <b-img v-if="user.profile_picture" rounded="circle" :src="user.profile_picture" width="50" height="50" blank-color="#777" alt="img" class="m-1" />
                  <b-button v-b-modal.modalUploadImage>
                    <translate>Change Photo</translate>
                  </b-button>
                </div>
                <div v-show="!user.profile_picture">
                  <b-img v-if="!user.profile_picture" rounded="circle" src="https://www.gravatar.com/avatar/default?s=50&r=pg&d=mm" width="50" height="50" blank-color="#777" alt="img" class="m-1" />
                  <b-button v-b-modal.modalUploadImage v-translate>Add Photo</b-button>
                </div>
              </div>
              <div class="form-group col-lg-6">
                <label class="control-label" v-translate>Mobile Phone</label>
                <b-form-input v-if="!user.phone" class="form-control" disabled />
                <cleave ref="phoneControl" :options="{ phone: true, phoneRegionCode: this.$cookies.get('country').toLowerCase() }" class="form-control" :value="user.phone" v-else disabled></cleave>
              </div>
              <div class="form-group col-lg-6">
                <label class="control-label d-none d-lg-block">&nbsp;</label>
                <b-btn v-b-modal.modalEnterPhone block class="" v-if="user.phone" v-translate>Change your mobile phone</b-btn>
                <b-btn v-b-modal.modalEnterPhone block class="" v-if="!user.phone" v-translate>Enter and verify your mobile phone</b-btn>
              </div>
              <div class="form-group col-sm-6" v-if="user.profile_address.place.formatted_address !== null">
                <label for="from_extra" v-translate>Street and number</label>
                <div class="input-group">
                  <textarea class="form-control" type="text" :value="user.profile_address.place.formatted_address" disabled>{{user.profile_address.place.formatted_address}}</textarea>
                </div>
              </div>
              <div class="form-group col-sm-6 col-lg-3" v-if="user.profile_address.place.formatted_address !== null">
                <label for="address_extra" v-translate>Floor, door, etc.</label>
                <div class="input-group">
                  <b-form-input id="address_extra" name="address_extra" :value="user.profile_address.address_extra" class="" disabled />
                </div>
              </div>
              <div class="form-group col-lg-12">
                <label class="control-label d-none d-lg-block">&nbsp;</label>
                <b-btn v-if="user.profile_address_id !== null" v-b-modal.modalEnterAddress block v-translate>Change address</b-btn>
                <label class="control-label" v-if="user.profile_address_id === null" v-translate>Address</label>
                <b-btn v-if="user.profile_address_id === null" v-b-modal.modalEnterAddress block v-translate>Enter your address</b-btn>
              </div>
            </div>
            <div class="row text-left">
              <div class="form-group col-12 form-actions">
                <label class="col-md-3 control-label"></label>
                <div class="col-md-8">
                  <b-button variant="primary" size="lg" type="submit" v-translate>Create courier</b-button>
                </div>
              </div>
            </div>
          </form>
        </b-card>
      </b-col>
      <b-col>
        <b-card class="user-vehicles">
          <h2 v-translate>Vehicles</h2>
          <b-table striped :items="user.vehicles" :fields="vehicleFields" v-if="user.vehicles && user.vehicles.length > 0">
            <template v-slot:cell(vehicle_type)="data">
              {{ vehicleTypes.find(x => x.id === data.item.vehicle_type_id).name}}
            </template>
            <template v-slot:cell(vehicle_card_picture)="data">
              <a :href="data.value" target="_blank" v-if="data.value" v-translate>YES</a>
              <span v-else v-translate>NO</span>
            </template>
            <template v-slot:cell(insurance_card_picture)="data">
              <a :href="data.value" target="_blank" v-if="data.value" v-translate>YES</a>
              <span v-else v-translate>NO</span>
            </template>
            <template v-slot:cell(actions)="data">
              <b-button pill size="sm" variant="danger" @click.stop="deleteVehicle(data.item)" style="padding: .5rem .65rem">
                <font-awesome-icon icon="trash-alt" size="2x" style="font-size: 16px;vertical-align: middle" />
              </b-button>
              <b-button pill size="sm" @click.stop="editingVehicle = data.item" v-b-modal.modalEditVehicle style="padding: .5rem .55rem">
                <font-awesome-icon :icon="['fa', 'pencil-alt']" size="2x" style="font-size: 16px;vertical-align: middle" />
              </b-button>
            </template>
          </b-table>
          <b-button v-b-modal.modalAddVehicle v-translate>Add Vehicle</b-button>
        </b-card>
      </b-col>
    </b-row>
    <b-modal id="modalUploadImage" ref="modalUploadImage" :hide-footer="true">
      <div slot="modal-title" v-translate>Add a new profile image</div>
      <div slot="modal-header-close"><img src="@/assets/img/ic_close.svg" alt="×" /></div>
        <profile-picture-uploader @updateProfileImage="updateProfileImage" />
    </b-modal>
    <b-modal id="modalEnterPhone" ref="modalEnterPhone" :hide-footer="true" @shown="focusPhoneInput">
      <div slot="modal-title" v-translate>Verify your mobile phone</div>
      <div slot="modal-header-close"><img src="@/assets/img/ic_close.svg" alt="×" /></div>
        <phone-validation ref="phoneValidation" :userId="user.id" @cancel="closeModalPhone" @success="successModalPhone" />
    </b-modal>
    <b-modal id="modalEnterAddress" ref="modalEnterAddress" :hide-footer="true" @shown="focusAddressInput">
      <div slot="modal-title" v-translate>Enter Your Address</div>
      <div slot="modal-header-close"><img src="@/assets/img/ic_close.svg" alt="×" /></div>
        <add-address ref="addAddress" :user="user" :ignoreArea="true" :isProfile="true" @cancel="closeModalAddress" @success="successModalAddress" />
    </b-modal>

    <b-modal id="modalAddVehicle" ref="modalAddVehicle" :hide-footer="true">
      <div slot="modal-title" v-translate>Add new vehicle to user</div>
      <div slot="modal-header-close"><img src="@/assets/img/ic_close.svg" alt="×" /></div>
      <vehicle-form :user="user" @success="editingVehicle = null; $refs.modalAddVehicle.hide(); fetchProfile();" @cancel="editingVehicle = null; $refs.modalAddVehicle.hide();" />
    </b-modal>

    <b-modal id="modalEditVehicle" ref="modalEditVehicle" :hide-footer="true">
      <div slot="modal-title" v-translate>Edit vehicle</div>
      <div slot="modal-header-close"><img src="@/assets/img/ic_close.svg" alt="×" /></div>
      <vehicle-form :user="user" :vehicle="editingVehicle" @success="editingVehicle = null; $refs.modalEditVehicle.hide(); getUser();" @cancel="editingVehicle = null; $refs.modalEditVehicle.hide();" />
    </b-modal>

  </b-container>
</template>
<script>
import ProfilePictureUploader from '@/components/ProfilePictureUploader'
import Cleave from 'vue-cleave'
import ProfileNavigation from '@/components/ProfileNavigation'
import AddAddress from '@/components/AddAddress'
import PhoneValidation from '@/components/PhoneValidation'
import Password from '@/components/Password'
import VehicleForm from '@/components/VehicleForm'

export default {
  name: 'ProfileEditPage',
  title() {
    return this.$gettext('Personal Info « Edit Profile')
  },
  components: {
    ProfilePictureUploader,
    Cleave,
    ProfileNavigation,
    PhoneValidation,
    AddAddress,
    Password,
    VehicleForm
  },
  data() {
    return {
      formSaved: false,
      dismissSecs: 10,
      dismissCountDown: 0,
      user: {
        name: '',
        email: '',
        tin: '',
        password: '',
        country_iso_code: null,
        profile_address: {
          place: {
            formatted_address: null
          }
        },
        profile_address_id: null,
        send_notifications: true,
        profile_picture: null,
        vehicles: []
      },
      validate: {
        phone: {
          number: '+' + this.$cookies.get('phonePrefix'),
          step: 1,
          code: null
        },
        address: {
          reference: 'profile',
          place_id: null,
          address_extra: null,
          favourite: false,
          display: null,
          id: null
        }
      },
      password: '',
      passwordConfirm: '',
      samePassword: null,
      passwordMinLength: 12,
      passwordMinScore: 100,
      newPasswordScore: 0,
      rolesSelected: [],
      step: 1,
      newVehicle: {
        reference: null,
        plate_number: null,
        vehicle_type_id: null,
        country_id: 1,
        active: true
      },
      vehicleFields: [
        {
          key: 'reference',
          label: this.$gettext('Reference')
        },
        {
          key: 'plate_number',
          label: this.$gettext('Plate Number')
        },
        {
          key: 'vehicle_type',
          label: this.$gettext('Type')
        },
        {
          key: 'insurance_expiration_date',
          label: this.$gettext('Insurance Exp.')
        },
        {
          key: 'vehicle_card_picture',
          label: this.$gettext('Vehicle Card')
        },
        {
          key: 'insurance_card_picture',
          label: this.$gettext('Insurance Card')
        },
        {
          key: 'actions',
          label: this.$gettext('Actions')
        }
      ],
      errors: '',
      editingVehicle: null
    }
  },
  watch: {
    'user.name': {
      handler (val) {
        this.user.name = this.$options.filters.capitalize(val)
      },
      deep: true
    }
  },
  created() {
    this.fetchCountries()
    this.fetchVehicleTypes()
    this.fetchRoles()
  },
  mounted() {
    this.user.country_iso_code = this.$cookies.get('country')
  },
  methods: {
    validatePassword () {
      this.samePassword = this.user.password.length > 0 ? this.user.password === this.passwordConfirm : null
    },
    addVehicle() {
      this.$http.post(this.$appConfig.api.baseUrl + 'users/' + this.user.id + '/vehicles', this.newVehicle)
        .then(response => {
          this.resetAddVehicleFields()
          this.fetchProfile()
        })
    },
    createCourier() {
      if (this.samePassword) {
        if (!this.user.tin) {
          delete this.user.tin
        }
        this.$http.post(this.$appConfig.api.baseUrl + 'users', this.user)
          .then(response => {
            this.user = response.body
            this.user['profile_address'] = {
              place: {
                formatted_address: null
              }
            }
            this.step = 2
          }, error => {
            let obj = {
              title: this.$gettext('ERROR'),
              message: error.body.message,
              type: 'error'
            }
            this.$parent.$refs.simplert.openSimplert(obj)
          })
      }
    },
    updateCourier() {
      this.errors = ''
      if (!this.user.profile_address_id) {
        this.errors += '<li>' + this.$gettext('You must set the courier\'s address') + '</li>'
      }
      if (!this.user.phone) {
        this.errors += '<li>' + this.$gettext('You must verify the courier\'s phone') + '</li>'
      }
      if (!this.user.vehicles || this.user.vehicles.length === 0) {
        this.errors += '<li>' + this.$gettext('You must enter at least one vehicle for the courier') + '</li>'
      }

      if (this.errors === '') {
        this.$http.post(this.$appConfig.api.baseUrl + 'users/' + this.user.id + '/roles', [this.roles.find(x => x.name.toLowerCase() === this.$roles.COURIER).id])
          .then(response => {
            this.$router.push('/list/couriers', { query: { created: true } })
          })
      }
    },
    updateProfileImage(url) {
      this.user.profile_picture = url
      this.$http.patch(this.$appConfig.api.baseUrl + 'users/' + this.user.id, { profile_picture: url }).then(response => {
        this.$refs.modalUploadImage.hide()
      }, error => {
        let obj = {
          title: this.$gettext('ERROR'),
          message: error.body.message,
          type: 'error'
        }
        this.$parent.$refs.simplert.openSimplert(obj)
      })
    },
    fetchProfile() {
      this.errors = ''
      this.$http.get(this.$appConfig.api.baseUrl + 'users/' + this.user.id)
        .then(response => {
          this.user = response.body
          if (!this.user.profile_address) {
            this.user['profile_address'] = {
              place: {
                formatted_address: null
              }
            }
          }
        })
    },
    deleteVehicle(vehicle) {
      if (
        confirm(this.$gettextInterpolate(this.$gettext('Are you sure you want to DELETE the vehicle with Reference:\n%{reference}\nWARNING: THIS ACTION CANNOT BE UNDONE!'), {reference: vehicle.reference}))
      ) {
        this.$http.delete(this.$appConfig.api.baseUrl + 'users/' + this.user.id + '/vehicles' + '/' + vehicle.id)
          .then(response => {
            this.fetchProfile()
          })
      }
    },
    focusPhoneInput() {
      this.$refs.phoneValidation.$refs.phoneInput.$el.focus()
    },
    closeModalPhone() {
      this.$refs.modalEnterPhone.hide()
    },
    successModalPhone() {
      this.fetchProfile()
      this.$refs.modalEnterPhone.hide()
    },
    focusAddressInput() {
      this.$refs.addAddress.$refs.addressAutocomplete.$el.children[0].focus()
    },
    closeModalAddress() {
      this.$refs.modalEnterAddress.hide()
    },
    successModalAddress() {
      this.fetchProfile()
      this.$refs.modalEnterAddress.hide()
    },
    resetAddVehicleFields() {
      this.$refs.modalAddVehicle.hide()
      Object.assign(this.$data.newVehicle, this.$options.data.call(this).newVehicle)
    },
    countDownChanged(dismissCountDown) {
      this.dismissCountDown = dismissCountDown
    },
    showAlert() {
      this.dismissCountDown = this.dismissSecs
    }
  }
}

</script>
<style lang="scss">
.form-control {
  background-position: right calc(0.375em + 0.1875rem) center;
}
.vue_component__upload--image {
  #upload_image_form--default {
    outline: 2px dashed grey;
    outline-offset: -10px;
    background: #e0ffff;
    color: dimgray;
    padding: 20px;
    min-height: 200px;
    position: relative;
    cursor: pointer;
  }

  &.dragover {
    #upload_image_form--default {
      background: #add8e6;

    }
  }

  .upload_image_form__thumbnails {

    .upload_image_form__thumbnail {
      span {
        top: 0;
        color: #fff;
        width: 20px;
        height: 20px;
      }
    }
  }
}

</style>
